import { TableType, TableViewType } from '@insights_module/types/LibraryTable';
import { GARM_CATEGORIES } from '@insights_module/constants/garm';

export const LIBRARY_TABLE_COLUMNS_STORAGE_KEY = 'insights.library-table-columns-v2';

// we changed column structure in the new version so we needed a new key to prevent users not being able to see new columns
// TODO: remove this after a month of releasing the new version
export const LIBRARY_TABLE_COLUMNS_STORAGE_KEY_OLD = 'insights.library-table-columns';

export const LIBRARY_TABLE_COLUMN_KEYS = {
  DETAILS: 'details',
  PUBLISHED_DATE: 'published_date',
  AUTHOR: 'author',
  DURATION: 'duration',
  AUDIO_COUNT: 'audio_count',
  MOST_RECENTLY_PUBLISHED_AUDIO_DATE: 'most_recently_published_audio_date',
  PODCAST_NAME: 'podcast_name',
  // STATUS: 'status', Temporarily commented column, because we do not have this information for audio.
  TYPE: 'type',
  // BRAND_SAFETY COLUMNS GARM
  ADULT_EXPLICIT_CONTENT: 'adult_explicit_content',
  ARMS_AMUNIION: 'arms_ammunition',
  CRIME_HARMFUL_ACT: 'crime_harmful_act',
  DEATH_INJURY_MILITARY_CONFLICT: 'death_injury_military_conflict',
  DEBATED_SOCIAL: 'debated_social',
  HATE_SPEECH: 'hate_speech',
  ILLEGAL_DRUGS: 'illegal_drugs',
  OBSCENITY: 'obscenity',
  ONLINE_PIRACY: 'online_piracy',
  SPAM_HARMFUL: 'spam_harmful',
  TERRORISM: 'terrorism',
  ADULT_CONTENT: 'adult_content',
  ARMS: 'arms',
  CRIME: 'crime',
  DEATH_INJURY: 'death_injury',
  DRUGS: 'drugs',
  MILITARY_CONFLICT: 'military_conflict',
  TABACOO: 'tobacco',
  // CONTEXTUAL COLUMNS
  CONTEXTUAL: 'contextual',
  // ...
};

interface Column {
  key: string;
  align: 'left' | 'center' | 'right';
}

interface LibraryTableColumns {
  groups: {
    info: Column[];
    brand_safety_garm: Column[];
    contextual: Column[];
  };
  audios: {
    info: Column[];
    brand_safety_garm: Column[];
    contextual: Column[];
  };
}

// TODO: Add all table columns keys in LIBRARY_TABLE_COLUMN_KEYS
export const libraryTableColumns: LibraryTableColumns = {
  groups: {
    info: [
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.DETAILS,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.AUTHOR,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.TYPE,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.AUDIO_COUNT,
        align: 'left',
      },
    ],
    brand_safety_garm: [
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.DETAILS,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE,
        align: 'left',
      },
      ...Object.values(GARM_CATEGORIES).map((category) => ({
        key: category,
        align: 'left' as 'left' | 'right' | 'center',
      })),
    ],
    contextual: [
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.DETAILS,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.MOST_RECENTLY_PUBLISHED_AUDIO_DATE,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.CONTEXTUAL,
        align: 'left',
      },
    ],
  },
  audios: {
    info: [
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.DETAILS,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE,
        align: 'center',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.DURATION,
        align: 'center',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.AUTHOR,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.PODCAST_NAME,
        align: 'left',
      },
      // Temporarily commented column, because we do not have this information for audio.
      // {
      //   key: LIBRARY_TABLE_COLUMN_KEYS.STATUS,
      //   align: 'left',
      // },
    ],
    brand_safety_garm: [
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.DETAILS,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE,
        align: 'center',
      },
      ...Object.values(GARM_CATEGORIES).map((category) => ({
        key: category,
        align: 'left' as 'left' | 'right' | 'center',
      })),
    ],
    contextual: [
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.DETAILS,
        align: 'left',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.PUBLISHED_DATE,
        align: 'center',
      },
      {
        key: LIBRARY_TABLE_COLUMN_KEYS.CONTEXTUAL, // TODO: Add contextual columns
        align: 'left',
      },
    ],
  },
};

const getColumnsKeys = (arr: Column[]) => arr.map((item) => item.key);

export const defaultLocalStorageColumns = {
  audios: [
    ...getColumnsKeys(libraryTableColumns.audios.info),
    ...getColumnsKeys(libraryTableColumns.audios.contextual),
    ...getColumnsKeys(libraryTableColumns.audios.brand_safety_garm),
  ],
  groups: [
    ...getColumnsKeys(libraryTableColumns.groups.info),
    ...getColumnsKeys(libraryTableColumns.groups.contextual),
    ...getColumnsKeys(libraryTableColumns.groups.brand_safety_garm),
  ],
};

export const useLibraryTableColumns = (selectedView: TableViewType, tableType: TableType) => {
  const columns = libraryTableColumns?.[tableType]?.[selectedView];

  return { columns };
};
